import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { IconInfo } from './icons';
import { Modal } from './ui';

interface ChangelogEntry {
  version: string;
  date: string;
  sections: { title: string; items: string[] }[];
}

interface VersionData {
  version: string;
  commit: string;
  builtAt: string | null;
  changelog: ChangelogEntry[];
}

/**
 * Versionshinweis mit Änderungsverlauf.
 *
 * Steht im Fuß der Seitenleiste – dort, wo eine Versionsnummer erwartet
 * wird, ohne dass sie den Betrieb stört. Ein Klick öffnet den vollständigen
 * Verlauf aus `CHANGELOG.md`, den das Backend unter `/api/version` mitliefert.
 */
export function VersionInfo({ compact = false }: { compact?: boolean }) {
  const [data, setData] = useState<VersionData | null>(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  // Der Verlauf in den Systemeinstellungen ist Superadmins vorbehalten; wer
  // dort nichts verloren hat, bekommt ihn als Fenster über dem Inhalt.
  const zuEinstellungen = !!user?.isSuperadmin;

  useEffect(() => {
    let active = true;
    api
      .get<VersionData>('/api/version')
      .then((result) => active && setData(result))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  if (!data) return null;

  return (
    <>
      <button
        className="version-info"
        onClick={() => (zuEinstellungen ? navigate('/admin/settings?tab=aenderungsverlauf') : setOpen(true))}
        title="Änderungsverlauf anzeigen"
      >
        <IconInfo width={13} height={13} />
        {!compact && <span>v{data.version}</span>}
      </button>

      {open && !zuEinstellungen && (
        <Modal title="Änderungsverlauf" onClose={() => setOpen(false)}>
          <div className="small subtle" style={{ marginBottom: 14 }}>
            Version {data.version} · Build {data.commit}
            {data.builtAt && ` · ${new Date(data.builtAt).toLocaleDateString('de-DE')}`}
          </div>
          {data.changelog.length === 0 && (
            <div className="small subtle">Kein Änderungsverlauf verfügbar.</div>
          )}
          <div className="stack" style={{ gap: 20 }}>
            {data.changelog.map((entry) => (
              <div key={entry.version}>
                <div className="row between" style={{ alignItems: 'baseline' }}>
                  <strong>Version {entry.version}</strong>
                  <span className="small subtle">{entry.date}</span>
                </div>
                {entry.sections.map((section, index) => (
                  <div key={section.title || index} style={{ marginTop: 8 }}>
                    {section.title && <div className="small" style={{ fontWeight: 600 }}>{section.title}</div>}
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      {section.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="small">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
}
